import React, { useCallback, useEffect, useState } from "react";
import AdminHeader from "./Header";
import "../../styles/Super.css";
import { useNotification } from "../../Toast/ToastProvider";
import { useNavigate, useParams } from "react-router-dom";
import {
  CustomColoredDropdown,
  formatMonthDateYear,
  formatShortMonthDateYearWithTime,
  getAdmin,
} from "../../include/function";
import { adminReqConfig, api, baseURL } from "../../include/api";
import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";
import { ChevronDown } from "react-feather";

const parseAuditValue = (value) => {
  const map = {};
  String(value || "")
    .split(";")
    .forEach((part) => {
      const index = part.indexOf("=");
      if (index > 0) map[part.slice(0, index)] = part.slice(index + 1);
    });
  return map;
};

const buildWeighSummary = (rows) => {
  if (!Array.isArray(rows) || rows.length === 0) return null;
  const sorted = [...rows].sort(
    (a, b) =>
      new Date(a.createTimestamp || 0) - new Date(b.createTimestamp || 0),
  );
  const first = parseAuditValue(sorted[0].oldValue);
  const last = parseAuditValue(sorted[sorted.length - 1].newValue);
  const hasWeight = first.weight != null || last.weight != null;
  const originalQty = Number(hasWeight ? first.weight : first.qty);
  const actualQty = Number(hasWeight ? last.weight : last.qty);
  const originalAmount = Number(
    first.amount != null ? first.amount : first.totalAmount,
  );
  const revisedAmount = Number(
    last.amount != null ? last.amount : last.totalAmount,
  );
  if (
    !Number.isFinite(originalQty) ||
    !Number.isFinite(actualQty) ||
    !Number.isFinite(originalAmount) ||
    !Number.isFinite(revisedAmount)
  ) {
    return null;
  }
  if (originalQty === actualQty && originalAmount === revisedAmount) return null;
  return {
    originalQty,
    actualQty,
    diff: actualQty - originalQty,
    originalAmount,
    revisedAmount,
    adjusted: originalAmount - revisedAmount,
  };
};

const formatKgLabel = (value) => {
  const number = Number(value);
  const text = Number.isInteger(number) ? String(number) : number.toFixed(2);
  return `${text} kg`;
};

const formatInr = (value) =>
  `₹${Number(value || 0).toLocaleString("en-IN", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })}`;

const WeighUpdateCard = ({ summary }) => {
  const [open, setOpen] = useState(false);
  const diffText = `${summary.diff > 0 ? "+" : ""}${formatKgLabel(summary.diff)}`;
  return (
    <div style={{ padding: "4px 0 8px" }}>
      <button
        type="button"
        onClick={() => setOpen((value) => !value)}
        style={{
          display: "inline-flex",
          alignItems: "center",
          gap: "6px",
          border: "1px solid #F5D98A",
          background: "#FFF6D8",
          color: "#1B3A6B",
          borderRadius: "8px",
          padding: "4px 10px",
          fontSize: "13px",
          fontWeight: 700,
          cursor: "pointer",
          marginBottom: open ? "10px" : 0,
        }}
      >
        <ChevronDown
          size={16}
          style={{
            transform: open ? "rotate(0deg)" : "rotate(-90deg)",
            transition: "transform 0.15s ease",
          }}
        />
        Edited
      </button>
      {open ? (
      <>
      <div
        style={{
          display: "flex",
          gap: "12px",
          alignItems: "flex-start",
          background: "#FFF6D8",
          borderRadius: "12px",
          padding: "14px 16px",
          marginBottom: "12px",
        }}
      >
        <span
          style={{
            width: "22px",
            height: "22px",
            borderRadius: "50%",
            background: "#F5B400",
            color: "#fff",
            display: "inline-flex",
            alignItems: "center",
            justifyContent: "center",
            fontWeight: 700,
            fontSize: "13px",
            flexShrink: 0,
            marginTop: "2px",
          }}
        >
          i
        </span>
        <div>
          <div style={{ fontWeight: 700, color: "#1B3A6B", fontSize: "15px" }}>
            Quantity Updated After Weighing
          </div>
          <div style={{ color: "#3D5A80", fontSize: "13px", marginTop: "2px" }}>
            The quantity shown below has been updated based on the actual
            weight measured at our warehouse. The final amount has been
            adjusted accordingly.
          </div>
        </div>
      </div>
      <div
        style={{
          display: "grid",
          gridTemplateColumns: "1fr 1fr",
          border: "1px solid #E6EEF8",
          borderRadius: "12px",
          overflow: "hidden",
          marginBottom: "12px",
        }}
      >
        <div style={{ padding: "14px 16px" }}>
          <div style={{ color: "#8A94A6", fontSize: "13px" }}>
            Original (Approx.) Quantity
          </div>
          <div style={{ fontWeight: 700, fontSize: "22px", color: "#1A1A1A" }}>
            {formatKgLabel(summary.originalQty)}
          </div>
        </div>
        <div
          style={{
            padding: "14px 16px",
            borderLeft: "1px solid #E6EEF8",
          }}
        >
          <div style={{ color: "#8A94A6", fontSize: "13px" }}>
            Actual Weighed Quantity
          </div>
          <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
            <span style={{ fontWeight: 700, fontSize: "22px", color: "#1F9D55" }}>
              {formatKgLabel(summary.actualQty)}
            </span>
            <span
              style={{
                background: "#E8F8EE",
                color: "#1F9D55",
                borderRadius: "999px",
                padding: "2px 8px",
                fontSize: "12px",
                fontWeight: 600,
              }}
            >
              {diffText}
            </span>
          </div>
        </div>
      </div>
      <div
        style={{
          display: "grid",
          gridTemplateColumns: "1fr 1fr 1fr",
          background: "#F4F8FC",
          borderRadius: "12px",
          padding: "14px 16px",
        }}
      >
        <div>
          <div style={{ color: "#8A94A6", fontSize: "13px" }}>
            Original Amount
            <br />
            (Approx.)
          </div>
          <div style={{ fontWeight: 700, color: "#1A1A1A", marginTop: "4px" }}>
            {formatInr(summary.originalAmount)}
          </div>
        </div>
        <div>
          <div style={{ color: "#8A94A6", fontSize: "13px" }}>
            Revised Amount
            <br />
            (Actual)
          </div>
          <div style={{ fontWeight: 700, color: "#1A1A1A", marginTop: "4px" }}>
            {formatInr(summary.revisedAmount)}
          </div>
        </div>
        <div>
          <div style={{ color: "#8A94A6", fontSize: "13px" }}>Amount Adjusted</div>
          <div style={{ fontWeight: 700, color: "#1F9D55", marginTop: "4px" }}>
            {formatInr(summary.adjusted)}
          </div>
        </div>
      </div>
      </>
      ) : null}
    </div>
  );
};

const InvoiceDetail = () => {
  const toast = useNotification();
  const { push } = useNavigate();
  const user = getAdmin();
  const { id } = useParams();
  const [order, setOrder] = useState(null);
  const [fetchLoading, setFetchLoading] = useState(true);
  const [paymentModalForItem, setPaymentModalForItem] = useState(null);
  const [customerDetail, setCustomerDetail] = useState([]);
  const [statusLoading, setStatusLoading] = useState(null);
  const [qtyDrafts, setQtyDrafts] = useState({});
  const [weightDrafts, setWeightDrafts] = useState({});
  const [qtySavingId, setQtySavingId] = useState(null);
  const [itemWeighSummaries, setItemWeighSummaries] = useState({});

  const [paymentData, setPaymentData] = useState({
    amountReceived: "",
    paymentDate: "",
    paymentType: "",
    notes: "",
  });

  const [attachmentModalOpen, setAttachmentModalOpen] = useState(false);
  const [attachmentData, setAttachmentData] = useState({
    attachmentName: "",
    url: null, 
  });
  const [attachmentLoading, setAttachmentLoading] = useState(false);

  const handleOpenAttachmentModal = () => {
    setAttachmentData({
      attachmentName: "",
      url: null,
    });
    setAttachmentModalOpen(true);
  };

  // Close Attachment Modal
  const handleCloseAttachmentModal = () => {
    setAttachmentModalOpen(false);
    setAttachmentData({
      attachmentName: "",
      url: null,
    });
  };

  // 🔹 Handle Attachment Form Submit (with file upload)
  const handleAttachmentSubmit = (e) => {
    e.preventDefault();

    if (!attachmentData.attachmentName || !attachmentData.url) {
      toast({ type: "error", message: "Please fill all required fields" });
      return;
    }

    setAttachmentLoading(true);

    // 🔹 Prepare FormData with file
    const formData = new FormData();
    formData.append("orderId", order?.id || ""); // Auto-filled from order state
    formData.append("attachmentName", attachmentData.attachmentName);
    formData.append("url", attachmentData.url); // ✅ Send File object as 'file'

    api
      .post("/order_attachment/", formData, {
        ...adminReqConfig(),
      })
      .then((response) => {
        if (response.status === 200 || response.status === 201) {
          toast({
            type: "success",
            message: "Attachment uploaded successfully!",
          });
          handleCloseAttachmentModal();
          getOrder(); // Refresh to show new attachment if listing implemented
        } else {
          toast({ type: "error", message: "Failed to upload attachment" });
        }
      })
      .catch((error) => {
        console.error("Attachment upload error:", error);
        toast({
          type: "error",
          message:
            error.response?.data?.message || "Error uploading attachment",
        });
      })
      .finally(() => {
        setAttachmentLoading(false);
      });
  };
  const handlePaymentSubmit = (itemToPay) => {
    if (!order) {
      toast({ type: "error", message: "Order data not available." });
      return;
    }
    if (!paymentData.amountReceived || !paymentData.paymentType) {
      toast({ type: "error", message: "Please fill in all required fields." });
      return;
    }

    if (!itemToPay) {
      toast({
        type: "error",
        message: "No specific item found to associate with payment.",
      });
      return;
    }

    const today = new Date().toISOString().split("T")[0]; // YYYY-MM-DD format

    const payload = {
      orderId: order.id,
      orderItemId: itemToPay.id,
      customerId: order.customerId,
      paymentMode: paymentData.paymentType,
      amount: parseFloat(paymentData.amountReceived).toFixed(2),
      createdBy: user.name || "Admin",
      paymentStatus: "paid",
      currency: "INR",
      notes: paymentData.notes || "",
      address: order.address || "",
      city: order.city || "",
      state: order.state || "",
      paymentDate: today,
    };
    console.log("Sending Payment Data for Item:", itemToPay.id, payload);

    api
      .post(`/payment/`, payload, adminReqConfig())
      .then((response) => {
        if (response.status === 200 || response.status === 201) {
          toast({ type: "success", message: "Payment added successfully!" });
        } else {
          toast({
            type: "error",
            message: `Payment API returned unexpected status: ${response.status}`,
          });
        }
      })
      .catch((error) => {
        console.error("Payment API Error:", error);
        console.error("Error Details:", error.response?.data || error.message);
        toast({
          type: "error",
          message: `Failed to add payment: ${
            error.response?.data?.message || error.message
          }`,
        });
      })
      .finally(() => {
        setPaymentData({
          amountReceived: "",
          paymentDate: "",
          paymentType: "",
          notes: "",
        });
        setPaymentModalForItem(null);
      });
  };

  const getOrder = useCallback(() => {
    setFetchLoading(true);
    api
      .get(`/orders/?id=${id}`, adminReqConfig())
      .then((response) => {
        if (response.status === 200) {
          const orderData = response.data.data[0] || null;
          setOrder(orderData);
          setQtyDrafts({});
          const items = orderData?.orderItems || [];
          if (!items.length) {
            setItemWeighSummaries({});
          } else {
            Promise.all(
              items.map((item) =>
                api
                  .get(
                    `/audit_log/?module=order_item&referenceId=${item.id}`,
                    adminReqConfig(),
                  )
                  .then((auditResponse) => ({
                    id: item.id,
                    rows: Array.isArray(auditResponse.data?.data)
                      ? auditResponse.data.data
                      : [],
                  }))
                  .catch(() => ({ id: item.id, rows: [] })),
              ),
            ).then((results) => {
              const next = {};
              results.forEach(({ id: itemId, rows }) => {
                const summary = buildWeighSummary(rows);
                if (summary) next[itemId] = summary;
              });
              setItemWeighSummaries(next);
            });
          }
          if (orderData?.customerId) {
            getCustomerDetail(orderData?.customerId);
          }
        }
      })
      .catch((error) => {
        toast({ type: "error", message: "Failed to load order." });
        push("/admin/sales/orders");
      })
      .finally(() => {
        setFetchLoading(false);
      });
  }, [id, user.id, push, toast]);

  useEffect(() => {
    getOrder();
  }, []);

  const handleDownloadPDF = () => {
    if (!order) return;
    const doc = new jsPDF({
      orientation: "portrait",
      unit: "mm",
      format: "a4",
    });
    // --- Title ---
    doc.setFontSize(16);
    doc.setFont("helvetica", "bold");
    doc.text("Invoice", 105, 15, { align: "center" });
    // --- Basic Info ---
    doc.setFontSize(10);
    doc.setFont("helvetica", "normal");
    doc.text(`Invoice ID: #${formatInvoiceId(order)}`, 14, 25);
    doc.text(`Date: ${formatMonthDateYear(order.createTimestamp)}`, 150, 25);
    // --- Customer Info ---
    doc.setFont("helvetica", "bold");
    doc.text("Customer Details:", 14, 35);
    doc.setFont("helvetica", "normal");
    doc.text(`Name: ${order.name || "N/A"}`, 14, 40);
    doc.text(`Customer Type: ${order.customerType || "N/A"}`, 14, 45);
    doc.text(`Payment Type: ${order.paymentType || "N/A"}`, 14, 50);
    // --- Delivery Address ---
    doc.setFont("helvetica", "bold");
    doc.text("Delivery Address:", 14, 60);
    doc.setFont("helvetica", "normal");
    const deliveryAddress =
      order.address ||
      order.landmark ||
      order.locality ||
      order.city ||
      order.state ||
      order.pin
        ? `${order.address || ""}, ${order.landmark || ""}, ${
            order.locality || ""
          }, ${order.city || ""}, ${order.state || ""} - ${order.pin || ""}`
        : "N/A";
    doc.text(deliveryAddress, 14, 65, { maxWidth: 180 });

    const tableBody = [];
    // --- Product Rows ---
    if (order.orderItems?.length > 0) {
      order.orderItems.forEach((item, i) => {
        const price = Number(item.price || 0);
        const qty = Number(item.qty || 0);
        const discount = Number(item.discountValue || 0);
        const productAmount = (price * qty).toFixed(2);
        const amount = (productAmount - discount).toFixed(2);
        tableBody.push([
          i + 1,
          `${item.productName || "N/A"} ${item.variant !== "" ? "(" : ""} ${item.variant ? item.variant : ""} ${item.variant !== "" ? ")" : ""}`,
          qty,
          `${price.toFixed(2)}`,
          `${discount}`,
          `${amount}`,
        ]);
      });
    }
    // --- Summary Rows ---
    tableBody.push(
      [
        { content: "Coupon Discount", colSpan: 5, styles: { halign: "right" } },
        { content: `-${Number(order.voucherDiscountAmount || 0).toFixed(2)}` },
      ],
      [
        {
          content: "Delivery Charges",
          colSpan: 5,
          styles: { halign: "right" },
        },
        { content: `${Number(order.totalDeliveryCharge || 0).toFixed(2)}` },
      ],
      [
        {
          content: "Total Amount",
          colSpan: 5,
          styles: { halign: "right", fontStyle: "bold" },
        },
        {
          content: `${Number(order.billAmount || 0).toFixed(2)}`,
          styles: { fontStyle: "bold" },
        },
      ],
    );
    // --- Table Render ---
    autoTable(doc, {
      startY: 70,
      head: [["S.No", "Product", "Qty", "Rate", "Discount", "Amount"]],
      body: tableBody,
      theme: "grid",
      styles: {
        fontSize: 9,
        cellPadding: 2,
        lineColor: [0, 0, 0],
        lineWidth: 0.1,
      },
      headStyles: {
        fillColor: [255, 255, 255],
        textColor: [0, 0, 0],
        fontStyle: "bold",
      },
      bodyStyles: {
        fillColor: [255, 255, 255],
        textColor: [0, 0, 0],
      },
    });
    // --- Footer ---
    const pageHeight = doc.internal.pageSize.getHeight();
    doc.setFontSize(8);
    doc.text("Thank you for shopping with us!", 105, pageHeight - 10, {
      align: "center",
    });
    doc.save(`Order_${formatInvoiceId(order)}.pdf`);
  };

  const totalQuantity = order?.orderItems?.reduce(
    (sum, item) => sum + Number(item.qty || 0),
    0,
  );
  const getCustomerDetail = useCallback((customerId) => {
    if (!customerId) return;
    setFetchLoading(true);
    api
      .get(`/customer/?id=${customerId}`, adminReqConfig())
      .then((response) => {
        if (response.status === 200) {
          setCustomerDetail(response.data.data[0] || null);
        }
      })
      .catch((error) => {
        toast({ type: "error", message: "Failed to load customer detail." });
      })
      .finally(() => {
        setFetchLoading(false);
      });
  }, []);

  if (fetchLoading) {
    return (
      <AdminHeader title="INVOICE DETAILS">
        <div className="content-wrapper p-0">
          <section
            style={{
              backgroundColor: "#F1F4F9",
              padding: "30px",
              marginTop: "70px",
              minHeight: "80vh",
            }}
          >
            <div className="text-center py-5">Loading...</div>
          </section>
        </div>
      </AdminHeader>
    );
  }

  if (!order) {
    return (
      <AdminHeader title="INVOICE DETAILS">
        <div className="content-wrapper p-0">
          <section
            style={{
              backgroundColor: "#F1F4F9",
              padding: "30px",
              marginTop: "70px",
              minHeight: "80vh",
            }}
          >
            <div className="text-center py-5 text-danger">
              Invoice not found
            </div>
          </section>
        </div>
      </AdminHeader>
    );
  }

  //Dropdown options
  const PAYMENT_OPTIONS = [
    { value: "paid", label: "Paid" },
    { value: "pending", label: "Unpaid" },
    { value: "partially paid", label: "Partially Paid" },
  ];

  const APPROVAL_OPTIONS = [
    { value: "approved", label: "Approved" },
    { value: "rejected", label: "Rejected" },
  ];

  const STATUS_OPTIONS = [
    { value: "ordered", label: "Recieved" },
    { value: "confirmed", label: "Confirmed" },
    { value: "packed", label: "Packed" },
    { value: "shipped", label: "Shipped" },
    { value: "delivered", label: "Delivered" },
    { value: "return", label: "Return" },
    { value: "cancelled", label: "Cancelled" },
  ];

  const getSellingPrice = (item) => {
    const unitPrice = Number(item.price || 0);
    const qty = Number(item.qty || 0);
    const discountValue = parseFloat(item.discountValue || 0);
    const lineAmount =
      item.amount != null
        ? Number(item.amount)
        : Math.max(0, unitPrice * qty - discountValue);
    if (qty > 0) {
      return Math.max(lineAmount / qty, 0);
    }
    return unitPrice;
  };

  const steelApproxKg = {
    6: 2.9,
    8: 4.815,
    10: 7.525,
    12: 10.825,
    16: 19.263,
    18: 24.35,
    20: 30.109,
    25: 46.932,
    32: 76.919,
  };

  const isNumberUnit = (unit) =>
    ["number", "numbers", "num", "nos"].includes(
      String(unit || "").toLowerCase(),
    );

  const roundedApproxKg = (item) => {
    const match = String(item?.variant || "").match(/(\d+(?:\.\d+)?)\s*mm/i);
    if (!match) return null;
    const raw = steelApproxKg[parseInt(match[1], 10)];
    if (raw == null) return null;
    return Math.round(raw);
  };

  const numberOrderedWeight = (item) => {
    const approx = roundedApproxKg(item);
    const qty = Number(item?.qty || 0);
    if (!approx || qty <= 0) return null;
    return qty * approx;
  };

  const numberSellingPrice = (item) => {
    const price = Number(item?.price || 0);
    const orderedWeight = numberOrderedWeight(item);
    const mode = String(item?.discountMode || "").toLowerCase();
    const discount = Number(item?.discountValue || 0);
    if (!orderedWeight || orderedWeight <= 0) return price;
    const actualPrice = orderedWeight * price;
    const discountAmount =
      mode === "percent" ? (actualPrice * discount) / 100 : discount;
    const discountedPrice = Math.max(0, actualPrice - discountAmount);
    return discountedPrice / orderedWeight;
  };

  const numberLineWeight = (item) => {
    const sellingPerKg = numberSellingPrice(item);
    const amount = Number(item?.amount || 0);
    if (sellingPerKg > 0 && amount > 0) {
      return Math.round((amount / sellingPerKg) * 100) / 100;
    }
    return numberOrderedWeight(item);
  };

  const canEditItemQty = (item) => {
    const status = String(item?.status || "").toLowerCase();
    return ![
      "cancelled",
      "return",
      "return approved",
      "return_approved",
      "rejected",
    ].includes(status);
  };

  const handleSaveItemQty = (item) => {
    const currentQty = Number(item.qty || 0);
    const nextQty = Number(qtyDrafts[item.id] ?? item.qty);
    if (!Number.isFinite(nextQty) || nextQty <= 0) {
      toast({ type: "error", message: "Quantity must be greater than 0" });
      return;
    }
    if (nextQty > currentQty) {
      toast({ type: "error", message: "Quantity increase is not allowed" });
      return;
    }
    if (nextQty === currentQty) {
      toast({ type: "error", message: "Quantity is unchanged" });
      return;
    }

    const sellingPrice = getSellingPrice(item);
    const deltaAmount = Number(
      ((item.amount != null ? Number(item.amount) : sellingPrice * currentQty) -
        sellingPrice * nextQty).toFixed(2),
    );
    const paymentStatus = String(order?.paymentStatus || "")
      .toLowerCase()
      .replace(/\s+/g, "-");
    const isPaid = paymentStatus === "paid";
    const isCredit = String(item.paymentMode || "").toLowerCase() === "credit";

    let extraNote = "Order quantity and amount will be updated.";
    if (isPaid) {
      extraNote = `₹${deltaAmount.toFixed(2)} will be added to customer wallet.`;
    } else if (isCredit) {
      extraNote = `₹${deltaAmount.toFixed(2)} will be reduced from customer credit.`;
    }

    if (
      !window.confirm(
        `Change quantity from ${currentQty} ${item.unit || ""} to ${nextQty} ${item.unit || ""}?\n${extraNote}`,
      )
    ) {
      return;
    }

    setQtySavingId(item.id);
    api
      .put(
        `/orders/?id=${order.id}&flag=updateOrderItemQty`,
        { itemId: item.id, qty: nextQty },
        adminReqConfig(),
      )
      .then((response) => {
        if (response.status === 200) {
          toast({
            type: "success",
            message: response.data.message || "Quantity updated",
          });
          getOrder();
        } else {
          toast({ type: "error", message: "Failed to update quantity" });
        }
      })
      .catch((error) => {
        toast({
          type: "error",
          message:
            error.response?.data?.message || "Failed to update quantity",
        });
      })
      .finally(() => setQtySavingId(null));
  };

  const handleSaveItemWeight = (item) => {
    const numbers = Number(item.qty || 0);
    const currentWeight = numberLineWeight(item);
    const nextWeight = Number(weightDrafts[item.id] ?? currentWeight);
    if (currentWeight == null) {
      toast({
        type: "error",
        message: "Unable to calculate current weight from amount and price",
      });
      return;
    }
    if (!Number.isFinite(nextWeight) || nextWeight <= 0) {
      toast({ type: "error", message: "Weight must be greater than 0" });
      return;
    }
    if (nextWeight > currentWeight) {
      toast({ type: "error", message: "Weight increase is not allowed" });
      return;
    }
    if (nextWeight === currentWeight) {
      toast({ type: "error", message: "Weight is unchanged" });
      return;
    }

    const oldAmount = Number(item.amount || 0);
    const ratePerKg = oldAmount / currentWeight;
    const newAmount = ratePerKg * nextWeight;
    const deltaAmount = Number((oldAmount - newAmount).toFixed(2));
    const paymentStatus = String(order?.paymentStatus || "")
      .toLowerCase()
      .replace(/\s+/g, "-");
    const isPaid = paymentStatus === "paid";
    const isCredit = String(item.paymentMode || "").toLowerCase() === "credit";

    let extraNote = "Numbers stay the same. Amount will be updated from the new weight.";
    if (isPaid) {
      extraNote = `Numbers stay ${numbers}. ₹${deltaAmount.toFixed(2)} will be added to customer wallet.`;
    } else if (isCredit) {
      extraNote = `Numbers stay ${numbers}. ₹${deltaAmount.toFixed(2)} will be reduced from customer credit.`;
    }

    if (
      !window.confirm(
        `Change weight from ${currentWeight} kg to ${nextWeight} kg?\nNumbers stay ${numbers}.\n${extraNote}`,
      )
    ) {
      return;
    }

    setQtySavingId(item.id);
    api
      .put(
        `/orders/?id=${order.id}&flag=updateOrderItemQty`,
        { itemId: item.id, weight: nextWeight },
        adminReqConfig(),
      )
      .then((response) => {
        if (response.status === 200) {
          toast({
            type: "success",
            message: response.data.message || "Weight updated",
          });
          setWeightDrafts((prev) => {
            const next = { ...prev };
            delete next[item.id];
            return next;
          });
          getOrder();
        } else {
          toast({ type: "error", message: "Failed to update weight" });
        }
      })
      .catch((error) => {
        toast({
          type: "error",
          message:
            error.response?.data?.message || "Failed to update weight",
        });
      })
      .finally(() => setQtySavingId(null));
  };

  const handleStatusChange = (orderId, itemId, value) => {
    // 🔹 FIX 1: Use state 'order' directly (it's a single object, not array)
    if (!order || order.id !== orderId) {
      toast({ type: "error", message: "Order not found" });
      return;
    }

    // Find the item to get current status
    const orderItem = order.orderItems?.find((item) => item.id === itemId);
    const currentStatus = orderItem?.status?.toLowerCase();

    // 🔹 If rejecting from need-approval
    if (value === "rejected") {
      setStatusLoading({ orderId, itemId });
      const payload = {
        status: "rejected",
        customerId: order.customerId,
      };
      const url = `/orders/?id=${orderId}&flag=changecreditstatus`;

      api
        .put(url, payload, adminReqConfig())
        .then((response) => {
          if (response.status === 200) {
            toast({ type: "success", message: "Order Rejected" });
            // 🔹 FIX 2: Update single object, not array
            setOrder((prev) => ({
              ...prev, // Spread existing order object
              orderItems: prev.orderItems.map((item) => ({
                ...item,
                status: "rejected", // Update all items
              })),
            }));
          } else {
            toast({ type: "error", message: "Failed to reject order" });
          }
        })
        .catch((error) => {
          console.error("Status update error:", error);
          toast({ type: "error", message: "Error updating status" });
        })
        .finally(() => setStatusLoading(null));
      return;
    }

    // 🔹 If approving from need-approval
    if (value === "approved") {
      setStatusLoading({ orderId, itemId });
      const payload = {
        status: "approved",
        customerId: order.customerId,
      };
      const url = `/orders/?id=${orderId}&flag=changecreditstatus`;

      api
        .put(url, payload, adminReqConfig())
        .then((response) => {
          if (response.status === 200) {
            toast({ type: "success", message: "Order Approved" });
            // 🔹 FIX 2: Update single object
            setOrder((prev) => ({
              ...prev,
              orderItems: prev.orderItems.map((item) => ({
                ...item,
                status: "ordered", // Move to "ordered" after approval
              })),
            }));
          } else {
            toast({ type: "error", message: "Failed to approve order" });
          }
        })
        .catch((error) => {
          console.error("Status update error:", error);
          toast({ type: "error", message: "Error updating status" });
        })
        .finally(() => setStatusLoading(null));
      return;
    }

    //Normal status change (for non need-approval statuses)
    setStatusLoading({ orderId, itemId });
    const payload = {
      status: value,
      customerId: order.customerId,
    };
    const url = `/orders/?id=${itemId}&flag=changestatus`;

    api
      .put(url, payload, adminReqConfig())
      .then((response) => {
        if (response.status === 200) {
          toast({ type: "success", message: "Status updated successfully" });
          setOrder((prev) => ({
            ...prev,
            orderItems: prev.orderItems.map((item) =>
              item.id === itemId ? { ...item, status: value } : item,
            ),
          }));
        } else {
          toast({ type: "error", message: "Failed to update status" });
        }
      })
      .catch((error) => {
        console.error("Status update error:", error);
        toast({ type: "error", message: "Error updating status" });
      })
      .finally(() => setStatusLoading(null));
  };

  const formatInvoiceId = (order) => {
    if (!order?.createTimestamp || !order?.id) return "INV--";

    const year = new Date(order.createTimestamp).getFullYear();
    const yy = String(year).slice(-2);
    const paddedId = String(order.id).padStart(4, "0");

    return `INV${yy}-${paddedId}`;
  };

  const formatOrderId = (order) => {
    if (!order?.createTimestamp || !order?.id) return "ORD--";

    // Extract last 2 digits of year
    const year = new Date(order.createTimestamp).getFullYear();
    const yy = String(year).slice(-2); // e.g., 2025 → "25"

    // Zero-pad order.id to 4 digits
    const paddedId = String(order.id).padStart(4, "0"); // e.g., 7 → "0007"

    return `ORD${yy}-${paddedId}`;
  };

  const getDaysCrossed = (timestamp) => {
    if (!timestamp) return 0;

    const orderDate = new Date(timestamp);
    const now = new Date();

    const diffTime = now.getTime() - orderDate.getTime();
    const diffDays = Math.floor(diffTime / (1000 * 60 * 60 * 24));

    return diffDays >= 0 ? diffDays : 0;
  };

  const getDisabledStatuses = (currentStatus) => {
    const status = currentStatus?.toLowerCase();
    const disabled = [];

    switch (status) {
      case "rejected":
        disabled.push(
          "ordered",
          "confirmed",
          "packed",
          "shipped",
          "delivered",
          "return",
          "cancelled",
          "approved",
        );
        break;
      case "ordered":
        break;
      case "confirmed":
        disabled.push("ordered", "approved", "rejected");
        break;
      case "packed":
        disabled.push("ordered", "confirmed", "approved", "rejected");
        break;
      case "shipped":
        disabled.push("ordered", "confirmed", "approved", "rejected", "packed");
        break;
      case "delivered":
        disabled.push(
          "ordered",
          "confirmed",
          "approved",
          "rejected",
          "packed",
          "shipped",
        );
        break;
      case "return":
        disabled.push(
          "ordered",
          "confirmed",
          "packed",
          "shipped",
          "delivered",
          "cancelled",
          "approved",
          "rejected",
        );
        break;
      case "cancelled":
        disabled.push(
          "ordered",
          "confirmed",
          "packed",
          "shipped",
          "delivered",
          "return",
          "approved",
          "rejected",
        );
        break;
      default:
        break;
    }
    return disabled;
  };

  return (
    <AdminHeader title="INVOICE DETAILS">
      <div className="content-wrapper p-0">
        <section
          style={{
            backgroundColor: "#F1F4F9",
            padding: "30px",
            marginTop: "70px",
            marginBottom: "40px",
            minHeight: "80vh",
          }}
        >
          {/* Middle Row: Customer Info + Address + Date */}
          <div className="row g-3 mb-14">
            <div className="col-8">
              <div
                className="order-detail-card shadow-sm"
                style={{
                  backgroundColor: "#fff",
                  borderRadius: "8px",
                  padding: "20px",
                }}
              >
                <div
                  className=" mb-3"
                  style={{
                    backgroundColor: "#fff",
                    display: "flex",
                    alignItems: "center",
                    gap: "16px",
                  }}
                >
                  <div className="d-flex align-items-center gap-3">
                    <h6
                      style={{
                        fontWeight: "700",
                        fontSize: "18px",
                        color: "#2c3e50",
                        margin: 0,
                      }}
                    >
                      Invoice ID{" "}
                      <span style={{ color: "#9da6b5" }}>
                        {formatInvoiceId(order)}
                      </span>
                    </h6>
                  </div>
                  <div style={{ display: "flex", gap: "10px" }}>
                    <CustomColoredDropdown
                      value={order?.orderItems[0].status}
                      options={STATUS_OPTIONS}
                      type="status"
                      disable={true}
                    />
                    <CustomColoredDropdown
                      value={order?.paymentStatus}
                      options={PAYMENT_OPTIONS}
                      type="payment"
                      disable={true}
                    />
                  </div>
                  <div style={{ display: "flex", gap: "10px" }}>
                    <button
                      style={{
                        color: "#fff",
                        backgroundColor: "#2b95d3",
                        borderRadius: "6px",
                        fontSize: "14px",
                        fontWeight: "700",
                        cursor: "pointer",
                        padding: "8px 16px",
                        minWidth: "150px",
                        height: "40px",
                        border: "none",
                        textWrap: "nowrap",
                      }}
                      fetchLoading={"loading..."}
                    >
                      Upload Invoice
                    </button>

                    <button
                      onClick={handleDownloadPDF}
                      style={{
                        color: "#2692d2",
                        backgroundColor: "#d9ebff",
                        borderRadius: "6px",
                        fontSize: "14px",
                        fontWeight: "700",
                        cursor: "pointer",
                        padding: "8px 16px",
                        minWidth: "150px",
                        height: "40px",
                        border: "none",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        gap: "6px",
                      }}
                    >
                      <img
                        src="/assets/images/pdf-dow.png"
                        alt="PDF"
                        style={{ height: "16px", width: "16px" }}
                      />
                      PDF
                    </button>
                  </div>
                </div>
                <div className="row g-4 mt-14">
                  <div className="col-3">
                    <div style={{ fontSize: "14px", color: "black" }}>
                      <strong>Customer Name</strong>
                      <p
                        style={{
                          margin: "4px 0",
                          fontSize: "16px",
                          fontWeight: "500",
                          color: "#6E7C94",
                        }}
                      >
                        {order?.name || "N/A"}
                      </p>
                    </div>

                    <div
                      style={{
                        fontSize: "14px",
                        color: "black",
                        marginTop: "30px",
                      }}
                    >
                      <strong>Billing Address</strong>
                      <p
                        style={{
                          margin: "4px 0",
                          fontSize: "14px",
                          fontWeight: "400",
                          color: "#6E7C94",
                          lineHeight: "1.4",
                        }}
                      >
                        {order?.billAddress ||
                        order?.billLandmark ||
                        order?.billLocality ||
                        order?.billCity ||
                        order?.billState ||
                        order?.billPin ? (
                          <>
                            {order?.billAddress && `${order?.billAddress}, `}
                            {order?.billLandmark && `${order?.billLandmark}, `}
                            {order?.billLocality && `${order?.billLocality}, `}
                            {order?.billCity && `${order?.billCity}, `}
                            <br />
                            {order?.billState && `${order?.billState}-`}
                            {order?.billPin}
                          </>
                        ) : (
                          "N/A"
                        )}
                      </p>
                    </div>
                  </div>

                  <div className="col-3">
                    <div style={{ fontSize: "14px", color: "black" }}>
                      <strong>Ordered Date & Time</strong>
                      <p
                        style={{
                          margin: "4px 0",
                          fontSize: "16px",
                          fontWeight: "500",
                          color: "#6E7C94",
                        }}
                      >
                        {formatShortMonthDateYearWithTime(
                          order?.createTimestamp,
                        )}
                      </p>
                    </div>
                  </div>

                  <div className="col-3">
                    <div
                      style={{
                        fontSize: "14px",
                        color: "black",
                      }}
                    >
                      <strong>Payment Type</strong>
                      <p
                        style={{
                          margin: "4px 0",
                          fontSize: "16px",
                          fontWeight: "500",
                          color: "#6E7C94",
                        }}
                      >
                        {order?.orderItems?.[0].paymentMode || "N/A"}
                      </p>
                    </div>

                    <div
                      style={{
                        fontSize: "14px",
                        color: "black",
                        marginTop: "30px",
                      }}
                    >
                      <strong>Delivery Address</strong>
                      <p
                        style={{
                          margin: "4px 0",
                          fontSize: "14px",
                          fontWeight: "400",
                          color: "#6E7C94",
                          lineHeight: "1.4",
                        }}
                      >
                        {order?.address},{order?.landmark},{order?.locality},
                        {order?.city},<br />
                        {order?.state}-{order?.pin}
                      </p>
                    </div>
                  </div>

                  <div className="col-3">
                    <div
                      style={{
                        fontSize: "14px",
                        color: "black",
                      }}
                    >
                      <strong>Billing Name</strong>
                      <p
                        style={{
                          margin: "4px 0",
                          fontSize: "16px",
                          fontWeight: "500",
                          color: "#6E7C94",
                        }}
                      >
                        {order?.billName || "N/A"}
                      </p>
                    </div>
                    <div
                      style={{
                        fontSize: "14px",
                        color: "black",
                        marginTop: "30px",
                      }}
                    >
                      <strong>Billing GST Number</strong>
                      <p
                        style={{
                          margin: "4px 0",
                          fontSize: "16px",
                          fontWeight: "500",
                          color: "#6E7C94",
                        }}
                      >
                        {order?.billGst || "N/A"}
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <div className="col-4">
              <div
                className="order-detail-card shadow-sm"
                style={{
                  backgroundColor: "#fff",
                  borderRadius: "8px",
                  padding: "27px",
                }}
              >
                <div
                  style={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                  }}
                >
                  <h6
                    style={{
                      fontWeight: "700",
                      fontSize: "18px",
                      color: "#2c3e50",
                    }}
                  >
                    Total Amount
                  </h6>
                </div>
                <p
                  style={{
                    background: "#d9ebff",
                    padding: "10px",
                    borderRadius: "5px",
                    fontWeight: "600",
                    color: "#6E7C95",
                    margin: 0,
                  }}
                >
                  ₹{" "}
                  {Number(order.billAmount || 0).toLocaleString(undefined, {
                    minimumFractionDigits: 2,
                    maximumFractionDigits: 2,
                  })}
                </p>
              </div>
              <div
                className="order-detail-card shadow-sm"
                style={{
                  backgroundColor: "#fff",
                  borderRadius: "8px",
                  padding: "20px",
                  marginTop: "24px",
                  marginBottom: "0px",
                }}
              >
                <h6
                  style={{
                    fontWeight: "700",
                    fontSize: "18px",
                    color: "#2c3e50",
                    marginBottom: "18px",
                  }}
                >
                  Total Quantity
                </h6>
                <p
                  style={{
                    background: "#d9ebff",
                    padding: "10px",
                    borderRadius: "5px",
                    fontWeight: "600",
                    color: "#6E7C95",
                    margin: 0,
                  }}
                >
                  {totalQuantity}
                </p>
              </div>
            </div>
          </div>
          {/* Customer Details */}
          <div className="row g-3 mb-3 ">
            <div className="col-8">
              <div
                className="order-detail-card shadow-sm"
                style={{
                  backgroundColor: "#fff",
                  borderRadius: "8px",
                  padding: "20px",
                }}
              >
                <div className="d-flex align-items-center gap-3">
                  <h6
                    style={{
                      fontWeight: "700",
                      fontSize: "18px",
                      color: "#2c3e50",
                      margin: 0,
                    }}
                  >
                    Customer Details
                  </h6>
                </div>
                <div className="row g-4 mt-14">
                  <div className="col-3">
                    <div style={{ fontSize: "14px", color: "black" }}>
                      <strong>Customer Name</strong>
                      <p
                        style={{
                          margin: "4px 0",
                          fontSize: "16px",
                          fontWeight: "500",
                          color: "#6E7C94",
                        }}
                      >
                        {customerDetail?.name || "Riyas Ahamed"}
                      </p>
                    </div>
                    <div
                      style={{
                        fontSize: "14px",
                        color: "black",
                        marginTop: "30px",
                      }}
                    >
                      <strong>Phone Number</strong>
                      <p
                        style={{
                          margin: "4px 0",
                          fontSize: "16px",
                          fontWeight: "500",
                          color: "#6E7C94",
                        }}
                      >
                        {customerDetail?.phoneNumber || "N/A"}
                      </p>
                    </div>
                  </div>

                  <div className="col-3">
                    <div style={{ fontSize: "14px", color: "black" }}>
                      <strong>Customer Type</strong>
                      <p
                        style={{
                          margin: "4px 0",
                          fontSize: "16px",
                          fontWeight: "500",
                          color: "#6E7C94",
                        }}
                      >
                        {customerDetail.customerType
                          ? `${customerDetail.customerType} (${
                              customerDetail.customerType === "individual"
                                ? "B2C"
                                : "B2B"
                            })`
                          : "N/A"}{" "}
                      </p>
                    </div>
                    <div
                      style={{
                        fontSize: "14px",
                        color: "black",
                        marginTop: "30px",
                      }}
                    >
                      <strong>Email ID</strong>
                      <p
                        style={{
                          margin: "4px 0",
                          fontSize: "16px",
                          fontWeight: "500",
                          color: "#6E7C94",
                        }}
                      >
                        {customerDetail?.emailId || "N/A"}
                      </p>
                    </div>
                  </div>

                  <div className="col-3">
                    <div style={{ fontSize: "14px", color: "black" }}>
                      <strong>Company Name</strong>
                      <p
                        style={{
                          margin: "4px 0",
                          fontSize: "16px",
                          fontWeight: "500",
                          color: "#6E7C94",
                        }}
                      >
                        {customerDetail?.shopName || "N/A"}
                      </p>
                    </div>
                    <div
                      style={{
                        fontSize: "14px",
                        color: "black",
                        marginTop: "30px",
                      }}
                    >
                      <strong>Address</strong>
                      <p
                        style={{
                          margin: "4px 0",
                          fontSize: "14px",
                          fontWeight: "400",
                          color: "#6E7C94",
                          lineHeight: "1.4",
                        }}
                      >
                        {customerDetail?.address1 || "N/A"},
                        {customerDetail?.address2 || "."}
                      </p>
                    </div>
                  </div>
                  <div className="col-3">
                    <div style={{ fontSize: "14px", color: "black" }}>
                      <strong>GST Number</strong>
                      <p
                        style={{
                          margin: "4px 0",
                          fontSize: "16px",
                          fontWeight: "500",
                          color: "#6E7C94",
                        }}
                      >
                        {customerDetail?.proof?.[0]?.gstNo || "N/A"}
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {order?.orderItems[0]?.paymentMode === "credit" ? (
              <div className="col-4">
                <div
                  className="order-detail-card shadow-sm mb-3"
                  style={{
                    backgroundColor: "#fff",
                    borderRadius: "8px",
                    padding: "20px",
                  }}
                >
                  {/* Credit Balance */}
                  <div className="">
                    <h6
                      style={{
                        fontWeight: "700",
                        fontSize: "18px",
                        color: "#2c3e50",
                        marginBottom: "12px",
                      }}
                    >
                      Credit Balance
                    </h6>
                    {order?.paymentStatus === "paid" ? (
                      <>
                        <div
                          style={{
                            height: "12px",
                            backgroundColor: "#e0e0e0",
                            borderRadius: "4px",
                            overflow: "hidden",
                            marginBottom: "8px",
                          }}
                        >
                          <div
                            style={{
                              width: "0%",
                              height: "100%",
                              backgroundColor: "#3498db",
                              borderRadius: "4px",
                            }}
                          ></div>
                        </div>
                        <div
                          style={{
                            display: "flex",
                            justifyContent: "space-between",
                          }}
                        >
                          <span
                            style={{
                              fontSize: "14px",
                              color: "#6E7C94",
                              fontWeight: "500",
                            }}
                          >
                            Nil
                          </span>
                          <span
                            style={{
                              fontSize: "14px",
                              color: "#6E7C94",
                              fontWeight: "500",
                            }}
                          >
                            Nil
                          </span>
                        </div>
                      </>
                    ) : (
                      <>
                        {(() => {
                          const currentCredit =
                            Number(customerDetail?.currentCredit) || 0;
                          const creditBal =
                            Number(customerDetail?.creditBal) || 0;
                          const isOverLimit = currentCredit > creditBal;

                          return (
                            <>
                              <div
                                style={{
                                  display: "flex",
                                  alignItems: "center",
                                  gap: "10px",
                                  marginBottom: "8px",
                                }}
                              >
                                <div
                                  style={{
                                    flex: 1,
                                    height: "12px",
                                    backgroundColor: "#e0e0e0",
                                    borderRadius: "4px",
                                    overflow: "hidden",
                                  }}
                                >
                                  <div
                                    style={{
                                      width: `${Math.min(
                                        (currentCredit / creditBal) * 100,
                                        100,
                                      )}%`,
                                      height: "100%",
                                      backgroundColor: isOverLimit
                                        ? "#D32B2E"
                                        : "#3498db",
                                      borderRadius: "4px",
                                    }}
                                  ></div>
                                </div>
                              </div>
                              <div
                                style={{
                                  display: "flex",
                                  justifyContent: "space-between",
                                }}
                              >
                                <span
                                  style={{
                                    fontSize: "14px",
                                    color: isOverLimit ? "#D32B2E" : "#6E7C94",
                                    fontWeight: "500",
                                  }}
                                >
                                  ₹{currentCredit.toLocaleString()}{" "}
                                  {isOverLimit && "Exceeds limit"}
                                </span>
                                {!isOverLimit ? (
                                  <span
                                    style={{
                                      fontSize: "14px",
                                      color: "#6E7C94",
                                      fontWeight: "500",
                                    }}
                                  >
                                    out of ₹{creditBal.toLocaleString()}
                                  </span>
                                ) : (
                                  ""
                                )}
                              </div>
                            </>
                          );
                        })()}
                      </>
                    )}
                  </div>
                  {/* Credit Limit (In Days) */}
                  <div className="mt-24 mb-12">
                    <h6
                      style={{
                        fontWeight: "700",
                        fontSize: "18px",
                        color: "#2c3e50",
                        marginBottom: "12px",
                      }}
                    >
                      Credit Limit (In Days)
                    </h6>
                    {order?.paymentStatus === "paid" ? (
                      <>
                        <div
                          style={{
                            height: "12px",
                            backgroundColor: "#e0e0e0",
                            borderRadius: "4px",
                            overflow: "hidden",
                            marginBottom: "8px",
                          }}
                        >
                          <div
                            style={{
                              width: "0%",
                              height: "100%",
                              backgroundColor: "#3498db",
                              borderRadius: "4px",
                            }}
                          ></div>
                        </div>
                        <div
                          style={{
                            display: "flex",
                            justifyContent: "space-between",
                          }}
                        >
                          <span
                            style={{
                              fontSize: "14px",
                              color: "#6E7C94",
                              fontWeight: "500",
                            }}
                          >
                            Nil
                          </span>
                          <span
                            style={{
                              fontSize: "14px",
                              color: "#6E7C94",
                              fontWeight: "500",
                            }}
                          >
                            Nil
                          </span>
                        </div>
                      </>
                    ) : (
                      <>
                        {(() => {
                          const daysCrossed = getDaysCrossed(
                            customerDetail?.creditDate,
                          );
                          const creditLimitDays =
                            Number(customerDetail?.creditLimit) || 10;
                          const isOverLimit = daysCrossed > creditLimitDays;

                          return (
                            <>
                              <div
                                style={{
                                  display: "flex",
                                  alignItems: "center",
                                  gap: "10px",
                                  marginBottom: "8px",
                                }}
                              >
                                <div
                                  style={{
                                    flex: 1,
                                    height: "12px",
                                    backgroundColor: "#e0e0e0",
                                    borderRadius: "4px",
                                    overflow: "hidden",
                                  }}
                                >
                                  <div
                                    style={{
                                      width: `${Math.min(
                                        (daysCrossed / creditLimitDays) * 100,
                                        100,
                                      )}%`,
                                      height: "100%",
                                      backgroundColor: isOverLimit
                                        ? "#D32B2E"
                                        : "#3498db", // Red if over
                                      borderRadius: "4px",
                                    }}
                                  ></div>
                                </div>
                              </div>
                              <div
                                style={{
                                  display: "flex",
                                  justifyContent: "space-between",
                                }}
                              >
                                <span
                                  style={{
                                    fontSize: "14px",
                                    color: isOverLimit ? "#D32B2E" : "#6E7C94",
                                    fontWeight: "500",
                                  }}
                                >
                                  {daysCrossed} days{" "}
                                  {isOverLimit && "Exceeds limit"}
                                </span>
                                <span
                                  style={{
                                    fontSize: "14px",
                                    color: "#6E7C94",
                                    fontWeight: "500",
                                  }}
                                >
                                  out of {creditLimitDays} days
                                </span>
                              </div>
                            </>
                          );
                        })()}
                      </>
                    )}
                  </div>

                  {(() => {
                    const currentCredit =
                      Number(customerDetail?.currentCredit) || 0;
                    const creditBal = Number(customerDetail?.creditBal) || 0;
                    const daysCrossed = getDaysCrossed(
                      customerDetail?.creditDate,
                    );
                    const creditLimitDays =
                      Number(customerDetail?.creditLimit) || 10;
                    const isOverBalance = currentCredit > creditBal;
                    const isOverDays = daysCrossed > creditLimitDays;

                    if (isOverBalance || isOverDays) {
                      return (
                        <p
                          style={{
                            marginTop: "16px",
                            fontSize: "13px",
                            color: "#6E7C94",
                            fontWeight: "",
                          }}
                        >
                          Limit will reset after total outstanding is cleared.
                        </p>
                      );
                    }
                    return null;
                  })()}
                </div>
              </div>
            ) : (
              <div className="col-md-4"></div>
            )}
          </div>

          <div
            className="ps-22 pe-22 pb-22 rounded-5"
            style={{ backgroundColor: "#fff" }}
          >
            <div className="row justify-content-between pt-32 pb-10">
              <div className="col-auto">
                <h6 className="mb-0">Attachment</h6>
              </div>

              <div className="col-auto d-flex justify-content-end gap-2">
                <button
                  className="btn me-0 mb-0"
                  style={{
                    height: "40px",
                    width: "140px",
                    color: "white",
                    backgroundColor: "#2696D5",
                    border: "none",
                    borderRadius: "6px",
                  }}
                  onClick={() => handleOpenAttachmentModal()}
                >
                  Add New
                </button>
              </div>
            </div>
            <div className="table-responsive mt-24 product-table">
              <table className="table table-striped custom-striped-table-product-list align-middle">
                <thead className="">
                  <tr>
                    <th scope="col">S.No</th>
                    <th scope="col">Attachment Name</th>
                    <th scope="col">File</th>
                  </tr>
                </thead>
                <tbody>
                  {order?.orderAttachment?.length > 0 ? (
                    order.orderAttachment.map((att, index) => (
                      <tr key={att.id || index}>
                        <td
                          style={{ paddingTop: "14px", paddingBottom: "14px" }}
                        >
                          {String(index + 1).padStart(2, "0")}
                        </td>
                        <td
                          style={{ paddingTop: "14px", paddingBottom: "14px" }}
                        >
                          {att.attachmentName}
                        </td>
                        <td
                          style={{ paddingTop: "14px", paddingBottom: "14px" }}
                        >
                          {att.url ? (
                            <a
                              href={baseURL + "/" + att.url}
                              target="_blank"
                              rel="noopener noreferrer"
                              onClick={(e) => {
                                e.preventDefault();
                                window.open(baseURL + "/" + att.url, "_blank");
                              }}
                              className="text-decoration-none fs-6"
                              style={{
                                color: "#2592D2",
                                fontWeight: "500",
                              }}
                            >
                              View File
                            </a>
                          ) : (
                            <span style={{ color: "#999" }}>No file</span>
                          )}
                        </td>
                      </tr>
                    ))
                  ) : (
                    <tr>
                      <td colSpan="3" className="text-center py-4">
                        No attachments found.
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>

          <div className="rounded-5 bg-white mt-24">
            <h6
              style={{
                fontWeight: "700",
                fontSize: "18px",
                color: "#2c3e50",
                marginBottom: "16px",
                padding: "20px",
              }}
            >
              Ordered Products
            </h6>
            <div className="table-responsive mt-24 order-table">
              <table className="table table-striped custom-striped-table-order-detail align-middle ">
                <thead>
                  <tr>
                    <th style={{ textWrap: "nowrap" }}>S.No</th>
                    <th style={{ textWrap: "nowrap" }}>Order ID</th>
                    <th>Product</th>
                    <th style={{ textWrap: "nowrap", minWidth: "160px" }}>
                      Quantity
                    </th>
                    <th>Price</th>
                    <th>Selling Price</th>
                    <th>Discount Rate</th>
                    <th>Status</th>
                    <th>Amount</th>
                  </tr>
                </thead>
                <tbody>
                  {order.orderItems && order.orderItems.length > 0 ? (
                    order.orderItems.map((item, index) => {
                      const unitPrice = Number(item.price || 0);
                      const qty = Number(item.qty || 0);
                      const discountRate = Number(item.discountValue || 0);
                      const discountValue = parseFloat(item.discountValue || 0);

                      const sellingPrice = getSellingPrice(item);
                      const draftQty = Number(qtyDrafts[item.id] ?? qty);
                      const lineWeight = numberLineWeight(item);
                      const draftWeight = Number(weightDrafts[item.id] ?? lineWeight);
                      const numberSell = isNumberUnit(item.unit)
                        ? numberSellingPrice(item)
                        : null;
                      const amount =
                        numberSell != null && Number.isFinite(draftWeight)
                          ? (numberSell * draftWeight).toFixed(2)
                          : (sellingPrice * draftQty).toFixed(2);

                      const qtyEditable = canEditItemQty(item);

                      const weighSummary = itemWeighSummaries[item.id];

                      return (
                        <React.Fragment key={item.id || index}>
                        <tr>
                          <td
                            style={{
                              paddingTop: "14px",
                              paddingBottom: "14px",
                            }}
                          >
                            {String(index + 1).padStart(2, "0")}
                          </td>
                          <td
                            style={{
                              paddingTop: "14px",
                              paddingBottom: "14px",
                              textWrap: "nowrap",
                            }}
                          >
                            {formatOrderId(order)}
                          </td>
                          <td
                            style={{
                              paddingTop: "14px",
                              paddingBottom: "14px",
                            }}
                          >
                            {item.productName || "N/A"}
                            {item.variant !== "" ? "(" : ""}
                            {item.variant ? item.variant : ""}
                            {item.variant !== "" ? ")" : ""}
                          </td>
                          <td
                            style={{
                              paddingTop: "14px",
                              paddingBottom: "14px",
                              minWidth: "180px",
                              whiteSpace: "nowrap",
                            }}
                          >
                            {qtyEditable && isNumberUnit(item.unit) ? (
                              <div
                                style={{
                                  display: "flex",
                                  alignItems: "center",
                                  gap: "6px",
                                  flexWrap: "nowrap",
                                }}
                              >
                                <span
                                  style={{
                                    whiteSpace: "nowrap",
                                    color: "#2c3e50",
                                    fontSize: "12px",
                                  }}
                                >
                                  {qty} Nos
                                </span>
                                <input
                                  type="number"
                                  min="0"
                                  step="0.01"
                                  value={
                                    weightDrafts[item.id] ??
                                    numberLineWeight(item) ??
                                    ""
                                  }
                                  disabled={qtySavingId === item.id}
                                  onChange={(e) =>
                                    setWeightDrafts((prev) => ({
                                      ...prev,
                                      [item.id]: e.target.value,
                                    }))
                                  }
                                  style={{
                                    width: "52px",
                                    height: "30px",
                                    border: "1px solid #c6d6f5",
                                    borderRadius: "6px",
                                    padding: "2px 6px",
                                    flexShrink: 0,
                                    fontSize: "13px",
                                  }}
                                />
                                <span
                                  style={{
                                    whiteSpace: "nowrap",
                                    color: "#6E7C94",
                                    fontSize: "12px",
                                  }}
                                >
                                  kg
                                </span>
                                <button
                                  type="button"
                                  title="Save"
                                  disabled={qtySavingId === item.id}
                                  onClick={() => handleSaveItemWeight(item)}
                                  style={{
                                    width: "30px",
                                    height: "30px",
                                    border: "none",
                                    borderRadius: "6px",
                                    backgroundColor: "#2696D5",
                                    color: "#fff",
                                    padding: 0,
                                    flexShrink: 0,
                                    cursor:
                                      qtySavingId === item.id
                                        ? "not-allowed"
                                        : "pointer",
                                  }}
                                >
                                  <i
                                    className={
                                      qtySavingId === item.id
                                        ? "fa fa-spinner fa-spin"
                                        : "fa fa-check"
                                    }
                                  />
                                </button>
                              </div>
                            ) : qtyEditable ? (
                              <div
                                style={{
                                  display: "flex",
                                  alignItems: "center",
                                  gap: "6px",
                                  flexWrap: "nowrap",
                                }}
                              >
                                <input
                                  type="number"
                                  min="0"
                                  step={
                                    String(item.unit || "")
                                      .toLowerCase()
                                      .includes("kg") ||
                                    String(item.unit || "")
                                      .toLowerCase()
                                      .includes("weight")
                                      ? "0.01"
                                      : "1"
                                  }
                                  value={qtyDrafts[item.id] ?? qty}
                                  disabled={qtySavingId === item.id}
                                  onChange={(e) =>
                                    setQtyDrafts((prev) => ({
                                      ...prev,
                                      [item.id]: e.target.value,
                                    }))
                                  }
                                  style={{
                                    width: "52px",
                                    height: "30px",
                                    border: "1px solid #c6d6f5",
                                    borderRadius: "6px",
                                    padding: "2px 6px",
                                    flexShrink: 0,
                                    fontSize: "13px",
                                  }}
                                />
                                <span
                                  style={{
                                    whiteSpace: "nowrap",
                                    flexShrink: 0,
                                    color: "#6E7C94",
                                    fontSize: "12px",
                                  }}
                                >
                                  {{
                                    kilograms: "kg",
                                    kg: "kg",
                                    qty: "Qty",
                                    numbers: "Nos",
                                    number: "Nos",
                                  }[item.unit?.toLowerCase()] ||
                                    item.unit ||
                                    ""}
                                </span>
                                <button
                                  type="button"
                                  title="Save"
                                  disabled={qtySavingId === item.id}
                                  onClick={() => handleSaveItemQty(item)}
                                  style={{
                                    width: "30px",
                                    height: "30px",
                                    border: "none",
                                    borderRadius: "6px",
                                    backgroundColor: "#2696D5",
                                    color: "#fff",
                                    padding: 0,
                                    flexShrink: 0,
                                    cursor:
                                      qtySavingId === item.id
                                        ? "not-allowed"
                                        : "pointer",
                                  }}
                                >
                                  <i
                                    className={
                                      qtySavingId === item.id
                                        ? "fa fa-spinner fa-spin"
                                        : "fa fa-check"
                                    }
                                  />
                                </button>
                              </div>
                            ) : (
                              <span style={{ whiteSpace: "nowrap" }}>
                                {qty} {item.unit || ""}
                              </span>
                            )}
                          </td>

                          <td
                            style={{
                              paddingTop: "14px",
                              paddingBottom: "14px",
                            }}
                          >
                            ₹
                            {unitPrice.toLocaleString(undefined, {
                              minimumFractionDigits: 2,
                            })}
                          </td>
                          <td>
                            ₹
                            {(numberSell != null ? numberSell : sellingPrice).toLocaleString(undefined, {
                              minimumFractionDigits: 2,
                            })}
                          </td>
                          <td
                            style={{
                              paddingTop: "14px",
                              paddingBottom: "14px",
                            }}
                          >
                            {item.discountMode === "percent" ? (
                              <>{discountRate}%</>
                            ) : (
                              <>₹{discountRate}</>
                            )}
                          </td>
                          <td>
                            {item.status?.toLowerCase() === "need-approval" ? (
                              <CustomColoredDropdown
                                value={item.status}
                                options={APPROVAL_OPTIONS}
                                disabledOptions={[]}
                                type="approval"
                                loading={
                                  statusLoading?.orderId === order.id &&
                                  statusLoading?.itemId === item.id
                                }
                                onChange={(val) =>
                                  handleStatusChange(order.id, item.id, val)
                                }
                              />
                            ) : (
                              <CustomColoredDropdown
                                value={item.status}
                                options={STATUS_OPTIONS.filter(
                                  (opt) => opt.value !== "need-approval",
                                )}
                                disabledOptions={getDisabledStatuses(
                                  item.status,
                                )}
                                type="status"
                                loading={
                                  statusLoading?.orderId === order.id &&
                                  statusLoading?.itemId === item.id
                                }
                                onChange={(val) =>
                                  handleStatusChange(order.id, item.id, val)
                                }
                              />
                            )}
                          </td>
                          <td
                            style={{
                              paddingTop: "14px",
                              paddingBottom: "14px",
                              textAlign: "right",
                            }}
                          >
                            ₹
                            {amount.toLocaleString(undefined, {
                              minimumFractionDigits: 2,
                            })}
                          </td>
                        </tr>
                        {weighSummary ? (
                          <tr>
                            <td
                              colSpan={9}
                              style={{
                                background: "#fff",
                                padding: "4px 16px 16px",
                              }}
                            >
                              <WeighUpdateCard summary={weighSummary} />
                            </td>
                          </tr>
                        ) : null}
                        </React.Fragment>
                      );
                    })
                  ) : (
                    <tr>
                      <td colSpan="10">No products found.</td>{" "}
                    </tr>
                  )}
                </tbody>
                <tfoot>
                  <tr>
                    <td
                      colSpan="8"
                      className="custom-striped-table-product-list-tfoot-td"
                    >
                      Coupon Discount
                    </td>
                    <td
                      style={{
                        paddingTop: "14px",
                        paddingBottom: "14px",
                        paddingLeft: "16px",
                        paddingRight: "16px",
                        fontWeight: "600",
                        color: "#808ca2",
                        textAlign: "right",
                        textWrap: "nowrap",
                      }}
                    >
                      {" "}
                      -₹
                      {Number(order.voucherDiscountAmount || 0).toLocaleString(
                        undefined,
                        {
                          minimumFractionDigits: 2,
                        },
                      )}
                    </td>
                  </tr>
                  <tr>
                    <td
                      colSpan="8"
                      className="custom-striped-table-product-list-tfoot-td"
                      style={{ background: "#fff" }}
                    >
                      Delivery Charges
                    </td>
                    <td
                      style={{
                        paddingTop: "14px",
                        paddingBottom: "14px",
                        paddingLeft: "16px",
                        paddingRight: "16px",
                        fontWeight: "600",
                        color: "#808ca2",
                        background: "#fff",
                        textAlign: "right",
                      }}
                    >
                      ₹
                      {Number(order.totalDeliveryCharge || 0).toLocaleString(
                        undefined,
                        {
                          minimumFractionDigits: 2,
                        },
                      )}
                    </td>
                  </tr>
                  <tr
                    style={{
                      fontSize: "16px",
                      fontWeight: "700",
                    }}
                  >
                    <td
                      colSpan="8"
                      className="custom-striped-table-product-list-tfoot-td"
                    >
                      Total Amount
                    </td>
                    <td
                      style={{
                        padding: "12px 16px",
                        textAlign: "right",
                      }}
                    >
                      ₹
                      {Number(order.billAmount || 0).toLocaleString(undefined, {
                        minimumFractionDigits: 2,
                      })}
                    </td>
                  </tr>
                </tfoot>
              </table>
            </div>
          </div>
        </section>
      </div>
      {paymentModalForItem && (
        <PaymentModal
          isOpen={!!paymentModalForItem}
          onClose={() => setPaymentModalForItem(null)}
          onSubmit={() => handlePaymentSubmit(paymentModalForItem)}
          paymentData={paymentData}
          setPaymentData={setPaymentData}
          item={paymentModalForItem}
        />
      )}
      {/*Attachment Modal */}
      <AttachmentModal
        isOpen={attachmentModalOpen}
        onClose={handleCloseAttachmentModal}
        onSubmit={handleAttachmentSubmit}
        attachmentData={attachmentData}
        setAttachmentData={setAttachmentData}
        loading={attachmentLoading}
      />
    </AdminHeader>
  );
};
const PaymentModal = ({
  isOpen,
  onClose,
  onSubmit,
  paymentData,
  setPaymentData,
  item,
}) => {
  if (!isOpen) return null;

  const today = new Date().toISOString().split("T")[0];

  const PAYMENT_TYPE_OPTIONS = [
    { value: "cash", label: "Cash" },
    { value: "card", label: "Card" },
    { value: "upi", label: "UPI" },
    { value: "bank_transfer", label: "Bank Transfer" },
    { value: "cheque", label: "Cheque" },
    { value: "COD", label: "Cash on Delivery" },
  ];

  return (
    <div
      style={{
        position: "fixed",
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        backgroundColor: "rgba(0, 0, 0, 0.5)",
        display: "flex",
        justifyContent: "center",
        alignItems: "center",
        zIndex: 9999,
      }}
      onClick={onClose}
    >
      <div
        style={{
          backgroundColor: "#fff",
          borderRadius: "8px",
          padding: "24px",
          width: "900px",
          maxWidth: "95vw",
          maxHeight: "90vh",
          overflowY: "auto",
          position: "relative",
        }}
        onClick={(e) => e.stopPropagation()}
      >
        <button
          onClick={onClose}
          style={{
            position: "absolute",
            top: "12px",
            right: "12px",
            width: "32px",
            height: "32px",
            background: "none",
            border: "3px solid #ddd",
            borderRadius: "50%",
            fontSize: "28px",
            cursor: "pointer",
            color: "#6E7C94",
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            padding: 0,
          }}
        >
          &times;
        </button>
        <h6
          style={{ marginBottom: "20px", fontWeight: "700", fontSize: "20px" }}
        >
          Add Payment for Item: {item?.productName || "N/A"} (Variant:{" "}
          {item?.variant || "N/A"})
        </h6>

        <div
          style={{
            display: "grid",
            gridTemplateColumns: "1fr 1fr",
            gap: "20px",
          }}
        >
          <div>
            <label
              htmlFor="amountReceived"
              style={{
                display: "block",
                marginBottom: "8px",
                fontWeight: "500",
                color: "#2c3e50",
              }}
            >
              Amount Received
            </label>
            <input
              id="amountReceived"
              type="number"
              step="0.01" // Allow decimal amounts
              placeholder="Enter the amount"
              value={paymentData.amountReceived}
              onChange={(e) =>
                setPaymentData({
                  ...paymentData,
                  amountReceived: e.target.value,
                })
              }
              style={{
                width: "100%",
                padding: "10px",
                borderRadius: "6px",
                border: "1px solid #ddd",
                fontSize: "14px",
              }}
              required
            />
          </div>
          <div>
            <label
              htmlFor="paymentDate"
              style={{
                display: "block",
                marginBottom: "8px",
                fontWeight: "500",
                color: "#2c3e50",
              }}
            >
              Payment Date
            </label>
            <input
              id="paymentDate"
              type="text"
              value={today}
              readOnly
              style={{
                width: "100%",
                padding: "10px",
                borderRadius: "6px",
                border: "1px solid #ddd",
                fontSize: "14px",
                backgroundColor: "#f8f9fa",
              }}
            />
          </div>
          {/* Payment Type */}
          <div>
            <label
              htmlFor="paymentType"
              style={{
                display: "block",
                marginBottom: "8px",
                fontWeight: "500",
                color: "#2c3e50",
              }}
            >
              Payment Type
            </label>
            <select
              id="paymentType"
              value={paymentData.paymentType}
              onChange={(e) =>
                setPaymentData({ ...paymentData, paymentType: e.target.value })
              }
              style={{
                width: "100%",
                padding: "10px",
                borderRadius: "6px",
                border: "1px solid #ddd",
                fontSize: "14px",
                appearance: "none",
                WebkitAppearance: "none",
                MozAppearance: "none",
              }}
              required
            >
              <option value="">Select</option>
              {PAYMENT_TYPE_OPTIONS.map((opt) => (
                <option key={opt.value} value={opt.value}>
                  {opt.label}
                </option>
              ))}
            </select>
          </div>
          {/* Notes - Changed to normal field */}
          <div>
            <label
              htmlFor="notes"
              style={{
                display: "block",
                marginBottom: "8px",
                fontWeight: "500",
                color: "#2c3e50",
              }}
            >
              Notes
            </label>
            <input
              id="notes"
              type="text"
              placeholder="Write notes here..."
              value={paymentData.notes}
              onChange={(e) =>
                setPaymentData({ ...paymentData, notes: e.target.value })
              }
              style={{
                width: "100%",
                padding: "10px",
                borderRadius: "6px",
                border: "1px solid #ddd",
                fontSize: "14px",
              }}
            />
          </div>
        </div>
        {/* Action Buttons */}
        <div
          style={{
            display: "flex",
            justifyContent: "flex-end",
            gap: "10px",
            marginTop: "20px",
          }}
        >
          <button
            onClick={onSubmit}
            style={{
              width: "150px",
              padding: "8px 16px",
              borderRadius: "6px",
              backgroundColor: "#2b95d3",
              color: "#fff",
              fontSize: "14px",
              fontWeight: "500",
              cursor: "pointer",
              border: "none",
            }}
          >
            Add
          </button>
          <button
            onClick={onClose}
            style={{
              width: "150px", // Added fixed width
              padding: "8px 16px",
              borderRadius: "6px",
              border: "none",
              backgroundColor: "#D9EBFF",
              color: "#3B9DD7",
              fontSize: "14px",
              fontWeight: "500",
              cursor: "pointer",
            }}
          >
            Clear All
          </button>
        </div>
      </div>
    </div>
  );
};

export default InvoiceDetail;


const AttachmentModal = ({
  isOpen,
  onClose,
  onSubmit,
  attachmentData,
  setAttachmentData,
  loading,
}) => {
  if (!isOpen) return null;

  const ATTACHMENT_OPTIONS = [
    { value: "", label: "Select Attachment Type" },
    { value: "ewaybill-1", label: "E-Way Bill-1" },
    { value: "ewaybill-2", label: "E-Way Bill-2" },
    { value: "taxinvoice-1", label: "Tax Invoice-1" },
    { value: "taxinvoice-2", label: "Tax Invoice-2" },
  ];

  return (
    <div
      style={{
        position: "fixed",
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        backgroundColor: "rgba(0, 0, 0, 0.5)",
        display: "flex",
        justifyContent: "center",
        alignItems: "center",
        zIndex: 9999,
      }}
      onClick={onClose}
    >
      <div
        style={{
          backgroundColor: "#fff",
          borderRadius: "8px",
          padding: "24px",
          width: "400px",
          maxWidth: "95vw",
          position: "relative",
        }}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Close Button */}
        <button
          onClick={onClose}
          style={{
            position: "absolute",
            top: "12px",
            right: "12px",
            width: "32px",
            height: "32px",
            background: "none",
            border: "3px solid #ddd",
            borderRadius: "50%",
            fontSize: "28px",
            cursor: "pointer",
            color: "#6E7C94",
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            padding: 0,
          }}
        >
          &times;
        </button>

        <h6 style={{ marginBottom: "20px", fontWeight: "700", fontSize: "20px" }}>
          Add Attachment
        </h6>

        <form onSubmit={onSubmit}>
          <div style={{ display: "flex", flexDirection: "column", gap: "16px" }}>
                        <div>
              <label style={{ display: "block", marginBottom: "8px", fontWeight: "500", color: "#2c3e50" }}>
                Attachment Type <span style={{ color: "red" }}>*</span>
              </label>
              <select
                value={attachmentData.attachmentName}
                onChange={(e) => setAttachmentData({ ...attachmentData, attachmentName: e.target.value })}
                style={{
                  width: "100%",
                  padding: "10px",
                  borderRadius: "6px",
                  border: "1px solid #ddd",
                  fontSize: "14px",
                }}
                required
              >
                {ATTACHMENT_OPTIONS.map((opt) => (
                  <option key={opt.value} value={opt.value}>
                    {opt.label}
                  </option>
                ))}
              </select>
            </div>

            <div>
              <label style={{ display: "block", marginBottom: "8px", fontWeight: "500", color: "#2c3e50" }}>
                Upload File <span style={{ color: "red" }}>*</span>
              </label>
              <input
                type="file"
                onChange={(e) => setAttachmentData({ ...attachmentData, url: e.target.files[0] })}
                style={{
                  width: "100%",
                  padding: "8px",
                  borderRadius: "6px",
                  border: "1px solid #ddd",
                  fontSize: "14px",
                }}
                accept=".pdf,.jpg,.jpeg,.png,.doc,.docx"
                required
              />
              <small style={{ color: "#6E7C94", fontSize: "12px" }}>
                Allowed: PDF, JPG, PNG, DOC (Max 5MB)
              </small>
              {attachmentData.url && (
                <small style={{ color: "#2b95d3", fontSize: "12px", display: "block", marginTop: "4px" }}>
                  Selected: {attachmentData.url.name}
                </small>
              )}
            </div>
          </div>

          {/* Action Buttons */}
          <div style={{ display: "flex", justifyContent: "flex-end", gap: "10px", marginTop: "24px" }}>
            <button
              type="button"
              onClick={onClose}
              style={{
                width: "100px",
                padding: "10px 16px",
                borderRadius: "6px",
                border: "none",
                backgroundColor: "#D9EBFF",
                color: "#3B9DD7",
                fontSize: "14px",
                fontWeight: "500",
                cursor: "pointer",
              }}
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={loading}
              style={{
                width: "100px",
                padding: "10px 16px",
                borderRadius: "6px",
                backgroundColor: loading ? "#999" : "#2b95d3",
                color: "#fff",
                fontSize: "14px",
                fontWeight: "500",
                cursor: loading ? "not-allowed" : "pointer",
                border: "none",
              }}
            >
              {loading ? "Uploading..." : "Upload"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};